<template>
  <div v-if="!showCompareChart" :class="['cl__compare', {'cl__compare--collapse': isCollapseHeader}]" v-focus-trap="!isCollapseHeader && products.length > 1">
    <button class="cl__compare-header" @click="toggleHeader" :aria-expanded="!isCollapseHeader">
      <span class="cl__compare-header-title">{{ products.length < 2 ? 'Add More Products to Compare' : `Compare products (${products.length} items)`}}</span>
      <svg class="cl__compare-arrow" aria-hidden="true">
        <use xlink:href="#chevron-down"></use>
      </svg>
    </button>
    <div class="cl__compare-body">
      <div v-if="!isCollapseHeader" class="cl__compare-products">
        <template v-for="index in windowWidth > 1023 ? compareProductDesktLimit : compareProductMobLimit" :key="index">  
          <div v-if="products[index-1]" class="cl__compare-product">
            <div class="cl__compare-product-image">
              <ImageField :key="products[index-1].title" :obj="{'url':productImage(products[index-1]),'alt':'Feature Image', 'width':332,'height':440}"/>
            </div>
            <span class="cl__compare-product-title">{{ products[index-1].title }}</span>
            <button class="cl__compare-product-remove" @click="onRemoveItem(products[index-1])" :aria-label="`Remove product ${products[index-1].title} from comparison`">
              <svg class="cl__compare-remove-icon" aria-hidden="true">
                <use xlink:href="#close"></use>
              </svg>
            </button>
          </div>
          <div v-else class="cl__compare-placeholder">Add Product to Compare</div>
        </template> 
      </div>
      <div :class="['cl__compare-ctas', {'cl__compare-ctas--hide': isCollapseHeader}]">
        <button @click="toggleCompareChart" :disabled="products.length < 2" class="btn btn--secondary cl__compare-btn">
          Compare
        </button>
        <button class="cl__compare-clear-btn" @click="emit('on-compare-clear')" aria-label="Clear all compared products">Clear all</button>
      </div>
    </div>
  </div>
 <CompareChart
   v-else
   :products="products"
   @toggle-compare-chart="toggleCompareChart"
   @on-remove-item="onRemoveItem"
 />
</template>

<script setup>
  import { ref, toRefs, watch } from 'vue';
  import ImageField from '../../global/image.vue';
  import CompareChart from '../../global/compare-chart/compare-chart.vue';
  import useWindowResize from '../../../utils/use-window-resize.js';

  const props = defineProps({
    products: {
      type: Object,
      required: true,
    }
  })

  const { products } = toRefs(props)
  const emit = defineEmits(['on-compare-click','on-compare-clear'])
  const isCollapseHeader = ref(false);
  const showCompareChart = ref(false)
  const { windowWidth } = useWindowResize();
  const compareProductDesktLimit = 4;
  const compareProductMobLimit = 3;

  const productImage = (product) => {
    const { variants, tags, available, Color, options, featured_image } = product;
    const firstOption = options[0]?.name;

    // Group variants by their first option (e.g., Color)
    const firstLevelOptions = variants.reduce((acc, variant) => {
      const option = variant.option1;
      if (!acc[option]) {
        acc[option] = { items: [], available: false };
      }
      acc[option].items.push(variant);
      if (variant.available) acc[option].available = true;
      return acc;
    }, {});

    // Process the grouped variants
    Object.entries(firstLevelOptions).forEach(([key, value]) => {
      const isAvailable = value.available;
      const bisData = tags.some(tag => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`));

      // Check if the variant is available or has BIS data or is globally unavailable
      if (isAvailable || bisData || !available) {
        firstOption === 'Color' && (value.image = value.items[0].featured_image);
      } else {
        delete firstLevelOptions[key];
      }
    });

    // Return the image of the selected color, or the default featured image
    return firstLevelOptions[Color]?.image || featured_image;
  };


  const toggleHeader = () => {
    isCollapseHeader.value = !isCollapseHeader.value;
  }

  const toggleCompareChart = () => {
    let bodyStyle = document.body.style;
    showCompareChart.value = !showCompareChart.value;

    if(showCompareChart.value) {
      bodyStyle.overflow = 'hidden'
    }else {
      bodyStyle.overflow = 'auto'
    }
  }

  const onRemoveItem = (product) => {
    if(products.value.length === 1) {
      document.body.style.overflow = 'auto'
    }
    emit('on-compare-click',false,product)
  }

  watch(products, () => {
    if(products.value.length === 1 && showCompareChart.value) {
      toggleCompareChart()
    }
  });
</script>